import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { logDev } from "@/lib/log";
import {
  CheckCircle2, AlertTriangle, Info, Send, CalendarClock, ArrowRight,
  BookOpen, PlayCircle, XCircle, PartyPopper,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

/**
 * Category → coloured card variant.
 * User-requested palette:
 *   • Telegram announcement          → #229ED9 (bright blue)
 *   • New coaching slot              → #0F172A (deep navy)
 *   • Coaching booking rejected      → #FF0022 (red)
 *   • Coaching booking confirmed     → #00FFAD (mint)
 *   • New ebook / e-video upload     → #FFFFFF (white)
 *   • Anything else                  → white (default)
 */
function classify(a) {
  if (a.source === "telegram") return "telegram";
  const kind = a.meta && a.meta.kind;
  if (kind === "coaching_slot" || (a.meta && a.meta.slot_id)) return "coaching_slot";
  if (kind === "booking_confirmed" || a.title === "Coaching booking confirmed") return "booking_confirmed";
  if (kind === "booking_rejected"  || a.title === "Coaching booking rejected")  return "booking_rejected";
  if (kind === "new_ebook") return "new_ebook";
  if (kind === "new_video") return "new_video";
  return "default";
}

const VARIANTS = {
  // Telegram — plain white box with bold blue border (per user spec).
  telegram: {
    bg: "#FFFFFF", borderColor: "#229ED9", borderWidth: 2, backdrop: false,
    text: "text-slate-900", muted: "text-slate-700", faint: "text-slate-400",
    iconBox: "bg-[#229ED9]/12 text-[#229ED9]", icon: Send,
    badgeCls: "bg-[#229ED9]/10 text-[#229ED9] border border-[#229ED9]/25",
    hoverBg: "hover:bg-[#229ED9]/[0.04]",
  },
  // Glass: soft tint + bold coloured border + backdrop blur.
  coaching_slot: {
    bg: "rgba(15, 23, 42, 0.06)", borderColor: "#0F172A", borderWidth: 2, backdrop: true,
    text: "text-slate-900", muted: "text-slate-700", faint: "text-slate-500",
    iconBox: "bg-[#0F172A] text-white", icon: CalendarClock,
    badgeCls: "bg-[#0F172A] text-white",
    hoverBg: "hover:bg-[#0F172A]/[0.10]",
  },
  booking_confirmed: {
    bg: "rgba(0, 255, 173, 0.12)", borderColor: "#00E69C", borderWidth: 2, backdrop: true,
    text: "text-slate-900", muted: "text-slate-700", faint: "text-slate-500",
    iconBox: "bg-[#00FFAD]/30 text-emerald-700", icon: PartyPopper,
    badgeCls: "bg-[#00FFAD]/30 text-emerald-800 border border-emerald-500/30",
    hoverBg: "hover:bg-[#00FFAD]/[0.20]",
  },
  booking_rejected: {
    bg: "rgba(255, 0, 34, 0.06)", borderColor: "#FF0022", borderWidth: 2, backdrop: true,
    text: "text-slate-900", muted: "text-slate-700", faint: "text-slate-500",
    iconBox: "bg-[#FF0022]/12 text-[#FF0022]", icon: XCircle,
    badgeCls: "bg-[#FF0022]/12 text-[#FF0022] border border-[#FF0022]/30",
    hoverBg: "hover:bg-[#FF0022]/[0.10]",
  },
  new_ebook: {
    bg: "#FFFFFF", borderColor: "#e2e8f0", borderWidth: 1, backdrop: false,
    text: "text-slate-900", muted: "text-slate-700", faint: "text-slate-400",
    iconBox: "bg-[#00b4ff]/10 text-[#00b4ff]", icon: BookOpen,
    badgeCls: "bg-[#00b4ff]/10 text-[#00b4ff]",
    hoverBg: "hover:bg-slate-50",
  },
  new_video: {
    bg: "#FFFFFF", borderColor: "#e2e8f0", borderWidth: 1, backdrop: false,
    text: "text-slate-900", muted: "text-slate-700", faint: "text-slate-400",
    iconBox: "bg-[#00b4ff]/10 text-[#00b4ff]", icon: PlayCircle,
    badgeCls: "bg-[#00b4ff]/10 text-[#00b4ff]",
    hoverBg: "hover:bg-slate-50",
  },
  default: {
    bg: "#FFFFFF", borderColor: "#e2e8f0", borderWidth: 1, backdrop: false,
    text: "text-slate-900", muted: "text-slate-700", faint: "text-slate-400",
    iconBox: "bg-slate-100 text-slate-600", icon: Info,
    badgeCls: "bg-slate-100 text-slate-600",
    hoverBg: "hover:bg-slate-50",
  },
};

// Type → icon fallback for the "default" variant so we still show a sensible glyph.
const TYPE_ICON = { success: CheckCircle2, warning: AlertTriangle, info: Info };

const BADGE_TEXT = {
  telegram: "via Telegram",
  coaching_slot: "tap to book",
  booking_confirmed: "confirmed",
  booking_rejected: "rejected",
  new_ebook: "new ebook",
  new_video: "new video",
};

function TelegramPhoto({ path }) {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    let url;
    (async () => {
      try {
        const res = await api.get(`/files/${path}`, { responseType: "blob" });
        url = URL.createObjectURL(res.data);
        setBlobUrl(url);
      } catch (err) { logDev(err); }
    })();
    return () => url && URL.revokeObjectURL(url);
  }, [path]);
  if (!blobUrl) {
    return <div className="mt-3 rounded-lg bg-black/10 animate-pulse" style={{ aspectRatio: "16/10" }} />;
  }
  return (
    <img
      src={blobUrl}
      alt=""
      className="mt-3 rounded-lg max-h-[380px] w-full max-w-full object-cover border border-slate-200"
      data-testid="announcement-photo"
    />
  );
}

export default function AnnouncementCard({ a, index = 0 }) {
  const variant = classify(a);
  const V = VARIANTS[variant];
  const isSlot = variant === "coaching_slot";
  const nav = useNavigate();
  const [expanded, setExpanded] = useState(false);

  const Icon = variant === "default"
    ? (TYPE_ICON[a.type] || Info)
    : V.icon;

  const openBooking = () => {
    const slotId = a.meta && a.meta.slot_id;
    if (!slotId) return;
    nav(`/education?tab=coaching&slot=${encodeURIComponent(slotId)}`);
  };

  // Every card is clickable: slot → opens booking, everything else → opens full-view modal.
  const handleOpen = () => { if (isSlot) openBooking(); else setExpanded(true); };
  const interactive = true;
  const baseCls = `stagger-item rounded-xl p-5 flex items-start gap-4 overflow-hidden ${V.text} cursor-pointer ${V.hoverBg}`;
  const glassStyle = {
    animationDelay: `${index * 40}ms`,
    backgroundColor: V.bg,
    borderStyle: "solid",
    borderWidth: `${V.borderWidth}px`,
    borderColor: V.borderColor,
    ...(V.backdrop ? { backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)" } : {}),
  };

  const rootProps = interactive
    ? {
        role: "button",
        tabIndex: 0,
        onClick: handleOpen,
        onKeyDown: (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleOpen(); } },
        className: baseCls,
        style: { ...glassStyle, transition: "background-color 0.2s ease" },
        "data-testid": `announcement-${a.id}`,
      }
    : {
        className: baseCls,
        style: glassStyle,
        "data-testid": `announcement-${a.id}`,
      };

  const badgeText = BADGE_TEXT[variant] || null;

  return (
    <>
    <div {...rootProps} data-variant={variant}>
      <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${V.iconBox}`}>
        <Icon size={16} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <div className={`font-display font-semibold ${V.text}`}>{a.title}</div>
          {badgeText && (
            <span
              className={`text-[9px] font-mono-plex tracking-[0.2em] uppercase px-1.5 py-0.5 rounded ${V.badgeCls}`}
              data-testid={`announcement-badge-${a.id}`}
            >
              {badgeText}
            </span>
          )}
          <div className={`text-[10px] tracking-widest font-mono-plex uppercase ${V.faint}`}>
            {new Date(a.created_at).toLocaleString()}
          </div>
        </div>
        {a.message && a.message !== "(photo)" && (
          <div className={`mt-1.5 text-sm leading-relaxed whitespace-pre-wrap break-words line-clamp-3 ${V.muted}`} style={{ overflowWrap: "anywhere" }}>{a.message}</div>
        )}
        {a.photo_path && <TelegramPhoto path={a.photo_path} />}
        {isSlot && (
          <div
            className={`mt-3 inline-flex items-center gap-1.5 text-[11px] font-display font-semibold ${V.text}`}
            data-testid={`announcement-slot-cta-${a.id}`}
          >
            Book coaching seat <ArrowRight size={12} />
          </div>
        )}
      </div>
    </div>
    <Dialog open={expanded} onOpenChange={setExpanded}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid={`announcement-modal-${a.id}`}>
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-lg grid place-items-center shrink-0 ${V.iconBox}`}>
              <Icon size={18} />
            </div>
            <div className="min-w-0 flex-1 text-left">
              <DialogTitle className="font-display text-lg leading-snug">{a.title}</DialogTitle>
              <DialogDescription className="font-mono-plex text-[10px] tracking-widest uppercase text-slate-500 mt-1">
                {new Date(a.created_at).toLocaleString()}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="mt-2 space-y-4">
          {a.message && a.message !== "(photo)" && (
            <div className="text-sm leading-relaxed whitespace-pre-wrap break-words text-slate-700" style={{ overflowWrap: "anywhere" }}>{a.message}</div>
          )}
          {a.photo_path && <TelegramPhoto path={a.photo_path} />}
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
